import { defineStore } from "pinia";
import { getESupplyVetSlides } from "@/api/services/esupplyvet/diaporamasService";
import { saveToCache, getFromCache, isCacheValid } from "@/utils/cacheUtils";

const CACHE_KEY = "slides";

/**
 * Transforme les données du web service en format optimisé pour le cache
 * @param {Array} slides - Données brutes du web service
 * @returns {Array} - Données optimisées
 */
function transformSlidesForCache(slides) {
  return slides.map(slide => ({
    id: slide.id,
    title: slide.title,
    content: slide.content,
    linkUrl: slide.linkUrl,
    active: slide.active,
    showTitle: slide.showTitle,
    titleColor: slide.titleColor,
    backgroundColor: slide.backgroundColor,
    buttonText: slide.buttonText,
    buttonTextColor: slide.buttonTextColor,
    buttonColor: slide.buttonColor,
    desktopImage: {
      path: slide.desktopImage?.path,
    },
  }));
}

export const useSlidesStore = defineStore("esupplyvet-slides", {
  state: () => ({
    slides: [],
    loading: false,
    error: null,
    lastFetch: null,
    isUpdatingInBackground: false,
  }),

  getters: {
    activeSlides: state => state.slides.filter(slide => slide.active),
    hasSlides: state => state.slides.length > 0,
    isLoading: state => state.loading,
    hasError: state => !!state.error,
    hasCachedData: state => state.slides.length > 0 && !state.loading,
  },

  actions: {
    /**
     * Charge les slides depuis le cache localStorage
     */
    loadFromCache() {
      const cachedSlides = getFromCache(CACHE_KEY);
      if (cachedSlides) {
        this.slides = cachedSlides;
        return true;
      }
      return false;
    },

    /**
     * Sauvegarde les slides dans le cache localStorage
     * @param {Array} slides - Slides à sauvegarder
     */
    saveToCache(slides) {
      const optimizedSlides = transformSlidesForCache(slides);
      saveToCache(CACHE_KEY, optimizedSlides);
    },

    /**
     * Récupère les slides avec stratégie cache-first
     * @param {boolean} force - Force le rechargement même si cache valide
     */
    async fetchSlides(force = false) {
      const hasCachedData = this.loadFromCache();
      if (hasCachedData && !force && isCacheValid(CACHE_KEY)) {
        this.isUpdatingInBackground = true;
        try {
          const response = await getESupplyVetSlides();
          const newSlides = response.data;
          this.saveToCache(newSlides);
          this.slides = transformSlidesForCache(newSlides);
          this.lastFetch = Date.now();
          this.error = null;
        } catch (error) {
          console.warn("Erreur lors de la mise à jour en arrière-plan:", error);
        } finally {
          this.isUpdatingInBackground = false;
        }
        return;
      }

      if (this.loading && !force) return;
      try {
        this.loading = true;
        this.error = null;
        const response = await getESupplyVetSlides();
        const newSlides = response.data;
        this.saveToCache(newSlides);
        this.slides = transformSlidesForCache(newSlides);
        this.lastFetch = Date.now();
      } catch (error) {
        console.error("Erreur lors du chargement des slides:", error);
        this.error = "Impossible de charger les slides";
        if (!hasCachedData) {
          throw error;
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Initialise le store avec cache-first
     */
    async initializeSlides() {
      // Charger immédiatement depuis le cache
      const hasCachedData = this.loadFromCache();
      await this.fetchSlides();
      return hasCachedData;
    },

    clearSlides() {
      this.slides = [];
      this.error = null;
      this.lastFetch = null;
      this.isUpdatingInBackground = false;
    },

    clearError() {
      this.error = null;
    },
  },
});
